/**
 * Platform-specific helper functions
 */

const PLATFORM_NAMES = {
  youtube: 'YouTube',
  twitch: 'Twitch',
  kick: 'Kick',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  twitter: 'Twitter',
  x: 'X',
};

/**
 * Get the correctly branded/capitalized platform name.
 * YouTube and TikTok have specific capitalization requirements per their brand guidelines.
 *
 * @param {string} platform - Raw platform name (any casing)
 * @returns {string} - Correctly branded name (e.g. "YouTube", "TikTok")
 */
export const formatPlatformName = (platform) => {
  if (!platform) return '';
  return PLATFORM_NAMES[platform.toLowerCase()] || platform;
};

/**
 * Get the correct follower terminology for a platform
 * YouTube uses "Subscribers", others use "Followers"
 *
 * @param {string} platform - Platform name (youtube, twitch, tiktok, instagram, etc.)
 * @param {boolean} plural - Whether to return plural form (default: true)
 * @returns {string} - "Subscribers" for YouTube, "Followers" for others
 */
export const getFollowerLabel = (platform, plural = true) => {
  if (!platform) return plural ? 'Followers' : 'Follower';

  const platformLower = platform.toLowerCase();

  if (platformLower === 'youtube') {
    return plural ? 'Subscribers' : 'Subscriber';
  }

  return plural ? 'Followers' : 'Follower';
};

/**
 * Get formatted follower count with correct terminology
 *
 * @param {number} count - Follower/subscriber count
 * @param {string} platform - Platform name
 * @returns {string} - Formatted string like "1.2M Subscribers" or "50K Followers"
 */
export const formatFollowerCount = (count, platform) => {
  if (!count) return `0 ${getFollowerLabel(platform)}`;

  const label = getFollowerLabel(platform);

  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M ${label}`;
  } else if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K ${label}`;
  }

  return `${count.toLocaleString()} ${label}`;
};
